/* eslint-disable fp/no-mutating-methods */
import {
  createAsyncThunk,
  createSlice as _createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import {
  Accreditation,
  ClassUpdated as Class,
  Course,
  HeroSlide,
  LearningPath,
  LinkedService,
  Quiz,
  Reference,
  Section,
  Series,
  ShowNotes,
  StreamStep,
  Template,
  TextContent,
  TitleScreen,
  Video,
} from 'src/v3/core/types'
import { LightweightCourseV5 } from 'src/v3/create/v5/course/types'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  getCourses,
  getReferences,
  getCategories,
  getTemplates,
} from './v5/utils/api'

export let fetchTemplates = createAsyncThunk(
  'create/fetchTemplatesStatus',
  () => axios.get<Template[]>('/api/v2/create/templates').then(pluckData),
)

export let fetchTemplatesV5 = createAsyncThunk(
  'create/fetchTemplatesV5Status',
  getTemplates,
)

export let fetchSections = createAsyncThunk('create/fetchSectionsStatus', () =>
  axios.get<Section[]>('/api/v2/create/sections').then(pluckData),
)

export let fetchCourses = createAsyncThunk('create/fetchCoursesStatus', () =>
  axios.get<Course[]>('/api/v2/create/courses').then(pluckData),
)

export let fetchCoursesV5 = createAsyncThunk(
  'create/fetchCoursesV5Status',
  getCourses,
)

export let fetchSectionsV5 = createAsyncThunk(
  'create/fetchSectionsV5Status',
  getCategories,
)

export let fetchReferences = createAsyncThunk(
  'create/fetchReferencesStatus',
  getReferences,
)

export let fetchLearningPathCourses = createAsyncThunk(
  'create/fetchLearningPathCoursesStatus',
  () =>
    axios.get<Course[]>('/api/v2/create/learning-path-courses').then(pluckData),
)

export let fetchClasses = createAsyncThunk('create/fetchClassesStatus', () =>
  axios.get<Class[]>('/api/v2/create/classes').then(pluckData),
)

export let fetchAccreditations = createAsyncThunk(
  'create/fetchAccreditationsStatus',
  () =>
    axios.get<Accreditation[]>('/api/v2/create/accreditations').then(pluckData),
)

export let fetchTitleScreens = createAsyncThunk(
  'create/fetchTitleScreenStatus',
  (id: string) =>
    axios
      .get<TitleScreen>(`/api/v2/create/title-screen?ClassID=${id}`)
      .then(pluckData),
)

export let fetchVideos = createAsyncThunk('create/fetchVideosStatus', () =>
  axios.get<Video[]>('/api/v2/create/video').then(pluckData),
)

export let fetchTextContent = createAsyncThunk(
  'create/fetchTextContentStatus',
  (id: string) =>
    axios
      .get<TextContent[]>(`/api/v2/create/text-content?ClassID=${id}`)
      .then(pluckData),
)

export let fetchShowNotes = createAsyncThunk(
  'create/fetchShowNotesStatus',
  (classID: string) =>
    axios
      .get<ShowNotes[]>(`/api/v2/create/show-notes/class/${classID}`)
      .then(pluckData),
)

export let createShowNotes = createAsyncThunk(
  'create/createShowNotesStatus',
  (showNotes: { classID: string; name: string; description: string }) =>
    axios
      .post<ShowNotes>('/api/v2/create/show-notes', showNotes)
      .then(pluckData),
)

export let fetchQuizzes = createAsyncThunk(
  'create/fetchQuizStatus',
  (id: string) =>
    axios.get<Quiz>(`/api/v2/create/quiz?ClassID=${id}`).then(pluckData),
)

export let fetchStoryboardSteps = createAsyncThunk(
  'create/fetchStoryboardStepsStatus',
  () => axios.get<StreamStep[]>('/api/v2/create/stream').then(pluckData),
)

export let fetchSeries = createAsyncThunk('create/fetchSeries', () =>
  axios.get<Series[]>(`/api/v2/create/series`).then(pluckData),
)

export let fetchLearningPaths = createAsyncThunk(
  'create/fetchLearningPaths',
  () =>
    axios.get<LearningPath[]>(`/api/v2/create/learning-paths`).then(pluckData),
)

export let fetchLinkedServices = createAsyncThunk(
  'linkedService/fetchLinkedServices',
  () =>
    axios
      .get<LinkedService[]>('/api/v2/create/linked-services')
      .then(pluckData),
)

export let fetchHeroSlides = createAsyncThunk('create/fetchHeroSlides', () =>
  axios.get<HeroSlide[]>('/api/create/slide').then(pluckData),
)

type EntityStatus = string | 'unselected'
type CreateState = {
  activeLinkedService: EntityStatus
  activeSection: EntityStatus
  activeCourse: EntityStatus
  activeClass: EntityStatus
  templatesStatus: Status
  templatesV5Status: Status
  sectionsStatus: Status
  sectionsV5Status: Status
  coursesStatus: Status
  coursesV5Status: Status
  learningPathCoursesStatus: Status
  classesStatus: Status
  accreditationsStatus: Status
  titleScreensStatus: Status
  videosStatus: Status
  textContentStatus: Status
  showNotesStatus: Status
  quizzesStatus: Status
  storyboardsStatus: Status
  templates: Template[]
  templatesV5: Record<string, Template[]>
  sections: Section[]
  sectionsV5: Record<string, Section[]>
  courses: Course[]
  coursesV5: LightweightCourseV5[]
  learningPathCourses: Course[]
  classes: Class[]
  accreditations: Accreditation[]
  titleScreens: Record<string, TitleScreen>
  videos: Video[]
  textContent: Record<string, TextContent[]>
  showNotes: Record<string, ShowNotes[]>
  quizzes: Record<string, Quiz>
  storyboardSteps: StreamStep[]
  series: Series[]
  seriesStatus: Status
  learningPaths: LearningPath[]
  learningPathsStatus: Status
  linkedServicesStatus: Status
  linkedServices: LinkedService[]
  heroSlides: HeroSlide[]
  heroSlidesStatus: Status
  references: Record<string, Reference[]>
  referencesStatus: Status
}

export let initialState: CreateState = {
  activeLinkedService: 'unselected',
  activeSection: 'unselected',
  activeCourse: 'unselected',
  activeClass: 'unselected',
  templatesStatus: 'uninitialized',
  templatesV5Status: 'uninitialized',
  sectionsStatus: 'uninitialized',
  sectionsV5Status: 'uninitialized',
  coursesStatus: 'uninitialized',
  coursesV5Status: 'uninitialized',
  learningPathCoursesStatus: 'uninitialized',
  classesStatus: 'uninitialized',
  accreditationsStatus: 'uninitialized',
  titleScreensStatus: 'uninitialized',
  videosStatus: 'uninitialized',
  textContentStatus: 'uninitialized',
  showNotesStatus: 'uninitialized',
  quizzesStatus: 'uninitialized',
  storyboardsStatus: 'uninitialized',
  templates: [],
  templatesV5: {},
  sections: [],
  sectionsV5: {},
  courses: [],
  coursesV5: [],
  learningPathCourses: [],
  classes: [],
  accreditations: [],
  titleScreens: {},
  videos: [],
  textContent: {},
  showNotes: {},
  quizzes: {},
  storyboardSteps: [],
  series: [],
  seriesStatus: 'uninitialized',
  learningPaths: [],
  learningPathsStatus: 'uninitialized',
  linkedServicesStatus: 'uninitialized',
  linkedServices: [],
  heroSlides: [],
  heroSlidesStatus: 'uninitialized',
  references: {},
  referencesStatus: 'uninitialized',
}

export let createSlice = _createSlice({
  name: 'create',
  initialState,
  reducers: {
    activeLinkedServiceChanged: (
      state,
      action: PayloadAction<EntityStatus>,
    ) => {
      state.activeLinkedService = action.payload
    },
    activeSectionChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeSection = action.payload
    },
    activeCourseChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeCourse = action.payload
    },
    activeClassChanged: (state, action: PayloadAction<EntityStatus>) => {
      state.activeClass = action.payload
    },
    sectionsChanged: (state, action: PayloadAction<Section[]>) => {
      state.sections = action.payload
    },
    titleScreensChanged: (state, action: PayloadAction<TitleScreen>) => {
      state.titleScreens[state.activeClass] = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.templatesStatus = 'pending'
      })
      .addCase(fetchTemplates.rejected, (state) => {
        state.templatesStatus = 'rejected'
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templatesStatus = 'fulfilled'
        state.templates = action.payload
      })
      .addCase(fetchTemplatesV5.pending, (state) => {
        state.templatesV5Status = 'pending'
      })
      .addCase(fetchTemplatesV5.rejected, (state) => {
        state.templatesV5Status = 'rejected'
      })
      .addCase(fetchTemplatesV5.fulfilled, (state, action) => {
        state.templatesV5Status = 'fulfilled'
        state.templatesV5 = action.payload
      })
      .addCase(fetchSections.pending, (state) => {
        state.sectionsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectionsStatus = 'rejected'
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.sectionsStatus = 'fulfilled'
        state.sections = action.payload
      })
      .addCase(fetchSectionsV5.pending, (state) => {
        state.sectionsV5Status = 'pending'
      })
      .addCase(fetchSectionsV5.rejected, (state) => {
        state.sectionsV5Status = 'rejected'
      })
      .addCase(fetchSectionsV5.fulfilled, (state, action) => {
        state.sectionsV5Status = 'fulfilled'
        state.sectionsV5 = action.payload
      })
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchCoursesV5.pending, (state) => {
        state.coursesV5Status = 'pending'
      })
      .addCase(fetchCoursesV5.rejected, (state) => {
        state.coursesV5Status = 'rejected'
      })
      .addCase(fetchCoursesV5.fulfilled, (state, action) => {
        state.coursesV5Status = 'fulfilled'
        state.coursesV5 = action.payload
      })
      .addCase(fetchLearningPathCourses.pending, (state) => {
        state.learningPathCoursesStatus = 'pending'
      })
      .addCase(fetchLearningPathCourses.rejected, (state) => {
        state.learningPathCoursesStatus = 'rejected'
      })
      .addCase(fetchLearningPathCourses.fulfilled, (state, action) => {
        state.learningPathCoursesStatus = 'fulfilled'
        state.learningPathCourses = action.payload
      })
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.classesStatus = 'fulfilled'
        state.classes = action.payload
      })
      .addCase(fetchAccreditations.pending, (state) => {
        state.accreditationsStatus = 'pending'
      })
      .addCase(fetchAccreditations.rejected, (state) => {
        state.accreditationsStatus = 'rejected'
      })
      .addCase(fetchAccreditations.fulfilled, (state, action) => {
        state.accreditationsStatus = 'fulfilled'
        state.accreditations = action.payload
      })
      .addCase(fetchTitleScreens.pending, (state) => {
        state.titleScreensStatus = 'pending'
      })
      .addCase(fetchTitleScreens.rejected, (state) => {
        state.titleScreensStatus = 'rejected'
      })
      .addCase(fetchTitleScreens.fulfilled, (state, action) => {
        state.titleScreensStatus = 'fulfilled'
        state.titleScreens[state.activeClass] = action.payload
      })
      .addCase(fetchVideos.pending, (state) => {
        state.videosStatus = 'pending'
      })
      .addCase(fetchVideos.rejected, (state) => {
        state.videosStatus = 'rejected'
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.videosStatus = 'fulfilled'
        state.videos = action.payload
      })
      .addCase(fetchTextContent.pending, (state) => {
        state.textContentStatus = 'pending'
      })
      .addCase(fetchTextContent.rejected, (state) => {
        state.textContentStatus = 'rejected'
      })
      .addCase(fetchTextContent.fulfilled, (state, action) => {
        state.textContentStatus = 'fulfilled'
        state.textContent[state.activeClass] = action.payload
      })
      .addCase(fetchShowNotes.pending, (state) => {
        state.showNotesStatus = 'pending'
      })
      .addCase(fetchShowNotes.rejected, (state) => {
        state.showNotesStatus = 'rejected'
      })
      .addCase(fetchShowNotes.fulfilled, (state, action) => {
        state.showNotesStatus = 'fulfilled'
        state.showNotes[state.activeClass] = action.payload
      })
      .addCase(createShowNotes.pending, () => {
        // No status update needed for create - just let it run
      })
      .addCase(createShowNotes.rejected, () => {
        // Could set an error state here if needed
      })
      .addCase(createShowNotes.fulfilled, (state, action) => {
        // Add the new ShowNotes to the current class array
        if (!state.showNotes[state.activeClass]) {
          state.showNotes[state.activeClass] = []
        }
        state.showNotes[state.activeClass].push(action.payload)
      })
      .addCase(fetchQuizzes.pending, (state) => {
        state.quizzesStatus = 'pending'
      })
      .addCase(fetchQuizzes.rejected, (state) => {
        state.quizzesStatus = 'rejected'
      })
      .addCase(fetchQuizzes.fulfilled, (state, action) => {
        state.quizzesStatus = 'fulfilled'
        state.quizzes[state.activeClass] = action.payload
      })
      .addCase(fetchStoryboardSteps.pending, (state) => {
        state.storyboardsStatus = 'pending'
      })
      .addCase(fetchStoryboardSteps.rejected, (state) => {
        state.storyboardsStatus = 'rejected'
      })
      .addCase(fetchStoryboardSteps.fulfilled, (state, action) => {
        state.storyboardsStatus = 'fulfilled'
        state.storyboardSteps = action.payload
      })
      .addCase(fetchSeries.pending, (state) => {
        state.seriesStatus = 'pending'
      })
      .addCase(fetchSeries.rejected, (state) => {
        state.seriesStatus = 'rejected'
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.seriesStatus = 'fulfilled'
        state.series = action.payload
      })
      .addCase(fetchLearningPaths.pending, (state) => {
        state.learningPathsStatus = 'pending'
      })
      .addCase(fetchLearningPaths.rejected, (state) => {
        state.learningPathsStatus = 'rejected'
      })
      .addCase(fetchLearningPaths.fulfilled, (state, action) => {
        state.learningPathsStatus = 'fulfilled'
        state.learningPaths = action.payload
      })
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchHeroSlides.pending, (state) => {
        state.heroSlidesStatus = 'pending'
      })
      .addCase(fetchHeroSlides.rejected, (state) => {
        state.heroSlidesStatus = 'rejected'
      })
      .addCase(fetchHeroSlides.fulfilled, (state, action) => {
        state.heroSlidesStatus = 'fulfilled'
        state.heroSlides = action.payload
      })
      .addCase(fetchReferences.pending, (state) => {
        state.referencesStatus = 'pending'
      })
      .addCase(fetchReferences.rejected, (state) => {
        state.referencesStatus = 'rejected'
      })
      .addCase(fetchReferences.fulfilled, (state, action) => {
        state.referencesStatus = 'fulfilled'
        state.references = action.payload
      })
  },
})

export let {
  activeLinkedServiceChanged,
  activeSectionChanged,
  activeCourseChanged,
  activeClassChanged,
  sectionsChanged,
  titleScreensChanged,
} = createSlice.actions

export let selectActiveLinkedService = (state: AppRootState) =>
  state.create.activeLinkedService
export let selectActiveSection = (state: AppRootState) =>
  state.create.activeSection
export let selectActiveCourse = (state: AppRootState) =>
  state.create.activeCourse
export let selectActiveClass = (state: AppRootState) => state.create.activeClass

export let selectTemplates = (state: AppRootState) => state.create.templates
export let selectTemplatesV5 = (state: AppRootState) => state.create.templatesV5
export let selectTemplatesStatus = (state: AppRootState) =>
  state.create.templatesStatus
export let selectTemplatesV5Status = (state: AppRootState) =>
  state.create.templatesV5Status
export let selectTemplatesByLinkedServiceID =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.templates.filter((t) => t.linkedServiceID == null),
      ...state.create.templates.filter((t) => t.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))
export let selectTemplatesByLinkedServiceV5 =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.create.templatesV5[linkedServiceID] || []

export let selectSections = (state: AppRootState) => state.create.sections
export let selectSectionsV5 = (state: AppRootState) => state.create.sectionsV5
export let selectSectionsByLinkedServiceV5 =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.create.sectionsV5[linkedServiceID] || []
export let selectAllSectionsV5 = (state: AppRootState): Section[] => {
  let grouped = state.create.sectionsV5
  let flattened: Section[] = []
  Object.values(grouped).forEach((sectionsArray) => {
    flattened.push(...sectionsArray)
  })
  return flattened.sort((a, b) => a.name.localeCompare(b.name))
}
export let selectSectionsStatus = (state: AppRootState) =>
  state.create.sectionsStatus
export let selectSectionsV5Status = (state: AppRootState) =>
  state.create.sectionsV5Status
export let selectSectionByID = (id: string) => (state: AppRootState) =>
  state.create.sections.find((s) => s.id === id)

export let selectCourses = (state: AppRootState) => state.create.courses
export let selectCoursesV5 = (state: AppRootState) => state.create.coursesV5
export let selectLearningPathCourses = (state: AppRootState) =>
  state.create.learningPathCourses
export let selectCoursesStatus = (state: AppRootState) =>
  state.create.coursesStatus
export let selectCoursesV5Status = (state: AppRootState) =>
  state.create.coursesV5Status
export let selectLearningPathCoursesStatus = (state: AppRootState) =>
  state.create.learningPathCoursesStatus
export let selectCoursesBySectionID = (id: string) => (state: AppRootState) =>
  state.create.courses
    .filter((c) => c.sectionID === id)
    .sort((a, b) => a.name.localeCompare(b.name))
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.create.courses.find((c) => c.id === id)

export let selectClasses = (state: AppRootState) => state.create.classes
export let selectClassesStatus = (state: AppRootState) =>
  state.create.classesStatus
export let selectClassesByCourseID = (id: string) => (state: AppRootState) =>
  state.create.classes
    .filter((c) => c.courseID === id)
    .sort((a, b) => a.sequence - b.sequence)
export let selectClassByID = (id: string) => (state: AppRootState) =>
  state.create.classes.find((c) => c.id === id)

export let selectAccreditations = (state: AppRootState) =>
  state.create.accreditations
export let selectAccreditationsStatus = (state: AppRootState) =>
  state.create.accreditationsStatus
export let selectAccreditationsByCourseID =
  (id: string) => (state: AppRootState) =>
    state.create.accreditations.filter(
      (a) =>
        (a.capce?.courseID ||
          a.ceBroker?.courseID ||
          a.newJersey?.courseID ||
          a.newYork?.courseID ||
          a.virginia?.courseID ||
          a.accredited?.courseID ||
          a.notAccredited?.courseID) === id,
    )

export let selectTitleScreenByClassID = (id: string) => (state: AppRootState) =>
  state.create.titleScreens[id]
export let selectTitleScreensStatus = (state: AppRootState) =>
  state.create.titleScreensStatus

export let selectVideos = (state: AppRootState) => state.create.videos

export let selectVideosByClassID = (id: string) => (state: AppRootState) =>
  state.create.videos.filter((v) => v.classID === id)
export let selectVideoByID = (id: string) => (state: AppRootState) =>
  state.create.videos.find((v) => v.id === id)
export let selectVideosStatus = (state: AppRootState) =>
  state.create.videosStatus

export let selectTextContentByClassID = (id: string) => (state: AppRootState) =>
  state.create.textContent[id]
export let selectTextContentStatus = (state: AppRootState) =>
  state.create.textContentStatus

export let selectShowNotesByClassID = (id: string) => (state: AppRootState) =>
  state.create.showNotes[id] || []
export let selectShowNotesStatus = (state: AppRootState) =>
  state.create.showNotesStatus

export let selectSectionsByLinkedService =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.sections.filter((s) => s.linkedServiceID == null),
      ...state.create.sections.filter((s) => s.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))

export let selectQuizByClassID = (id: string) => (state: AppRootState) =>
  state.create.quizzes[id]
export let selectQuizzesStatus = (state: AppRootState) =>
  state.create.quizzesStatus

export let selectStoryboardSteps = (state: AppRootState) =>
  state.create.storyboardSteps
export let selectStoryboardStepsByClassID =
  (id: string) => (state: AppRootState) =>
    state.create.storyboardSteps
      .filter((ss) => ss.classID === id)
      .sort((a, b) => a.stepIndex - b.stepIndex)
export let selectStoryboardStepsStatus = (state: AppRootState) =>
  state.create.storyboardsStatus

export let selectSeries = (state: AppRootState) => state.create.series
export let selectSeriesStatus = (state: AppRootState) =>
  state.create.seriesStatus

export let selectHeroSlides = (state: AppRootState) => state.create.heroSlides
export let selectHeroSlidesStatus = (state: AppRootState) =>
  state.create.heroSlidesStatus

export let selectLearningPathsByLinkedService =
  (id: string) => (state: AppRootState) =>
    [
      ...state.create.learningPaths.filter((s) => s.linkedServiceID == null),
      ...state.create.learningPaths.filter((s) => s.linkedServiceID === id),
    ].sort((a, b) => a.name.localeCompare(b.name))
export let selectLearningPathsStatus = (state: AppRootState) =>
  state.create.learningPathsStatus

export let selectLinkedServices = (state: AppRootState) =>
  state.create.linkedServices
export let selectLinkedServiceById = (id: string) => (state: AppRootState) =>
  state.create.linkedServices.find((x) => x.id === id)
export let selectLinkedServicesByIds =
  (ids: string[]) => (state: AppRootState) =>
    state.create.linkedServices.filter((x) => ids.includes(x.id ?? ''))
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.create.linkedServicesStatus

export let selectReferences = (state: AppRootState) => state.create.references
export let selectReferencesByLinkedService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.create.references[linkedServiceID] || []
export let selectReferencesStatus = (state: AppRootState) =>
  state.create.referencesStatus

export let resetActiveEntityOnChange =
  // eslint-disable-next-line consistent-return
  (store: any) => (next: any) => (action: any) => {
    if (action.type === 'create/activeLinkedServiceChanged') {
      store.dispatch(activeSectionChanged('unselected'))
      store.dispatch(activeCourseChanged('unselected'))
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action.type === 'create/activeSectionChanged') {
      store.dispatch(activeCourseChanged('unselected'))
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action.type === 'create/activeCourseChanged') {
      store.dispatch(activeClassChanged('unselected'))
    }

    if (action) {
      return next(action)
    }
  }

export default createSlice.reducer
